import { useCallback, useMemo, useState } from 'react'

import { Comparator, Order, SortConfig, UseTableProps } from './type'
/**
 * Custom hooks for sorting/pagination of tables.
 *
 * @param {number} defaultPage - First page to be displayed
 * @param {number} defaultRowsPerPage - Number of displays per page
 * @param {string} orderBy - The key of the object to be sorted
 * @param {T[]} rawItems - Raw data used in logic only
 * @param {T[]} rowItems - Items actually displayed in the table
 * @param {boolean} isInfiniteScroll - If true, the defaultRowsPerPage value is ignored
 */
export const useTable = <T>({
  defaultPage,
  defaultRowsPerPage,
  orderBy,
  rawItems,
  rowItems,
  isInfiniteScroll = false,
}: UseTableProps<T>) => {
  const [sortConfig, setSortConfig] = useState<SortConfig<T>>({
    order: false,
    orderBy: orderBy,
  })
  const [page, setPage] = useState(defaultPage)
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage)

  const descendingComparator = <T>(a: T, b: T, orderBy: keyof T) => {
    if (typeof a[orderBy] === 'number' && typeof b[orderBy] === 'number') {
      return Number(b[orderBy]) - Number(a[orderBy])
    }
    const sa = String(a[orderBy]).replace(/(\d+)/g, (m) => m.padStart(10, '0'))
    const sb = String(b[orderBy]).replace(/(\d+)/g, (m) => m.padStart(10, '0'))
    if (sb < sa) {
      return -1
    }
    if (sb > sa) {
      return 1
    }

    return 0
  }

  const compareElementsWithIndexFallback = <T>(
    a: readonly [T, number],
    b: readonly [T, number],
    comparator: (a: T, b: T) => number
  ) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) {
      return order
    }
    return a[1] - b[1]
  }

  const stableSortArray = <T>(
    array: T[],
    comparator: (a: T, b: T) => number
  ) => {
    const arrayWithIndex = array.map((el, index) => [el, index] as const)
    arrayWithIndex.sort((a, b) =>
      compareElementsWithIndexFallback(a, b, comparator)
    )

    return arrayWithIndex.map((el) => el[0])
  }

  const getComparator = <T, Key extends keyof T>(
    order: Order,
    orderBy: Key
  ): Comparator<T, Key> => {
    if (order === false) return () => 0
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy)
  }

  const onRequestSort = useCallback(
    (_event: React.MouseEvent<unknown>, property: keyof T) => {
      const isAsc =
        sortConfig.orderBy === property && sortConfig.order === 'asc'
      const isDesc =
        sortConfig.orderBy === property && sortConfig.order === 'desc'

      // It is necessary to return to a non-sorting state.
      // If order is false, the items are displayed in the order in which they were responded to
      setSortConfig({
        order: isAsc ? 'desc' : isDesc ? false : 'asc',
        orderBy: property,
      })
    },
    [setSortConfig, sortConfig.order, sortConfig.orderBy]
  )

  const createSortHandler = useCallback(
    (property: keyof T) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property)
    },
    [onRequestSort]
  )

  const direction = useCallback(
    (key: keyof T) => {
      if (!sortConfig.order) return undefined
      return key === sortConfig.orderBy ? sortConfig.order : 'asc'
    },
    [sortConfig.order, sortConfig.orderBy]
  )

  const displayItems = useMemo(() => {
    if (isInfiniteScroll) return rowItems
    return rowsPerPage > 0
      ? rowItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : rowItems
  }, [rowItems, page, rowsPerPage, isInfiniteScroll])

  const sliceItemsPerRowsPerPage = useCallback(
    (list: T[]) => {
      return list.reduce((acc: T[][], _value, index) => {
        if (index % rowsPerPage === 0) {
          acc.push(list.slice(index, index + rowsPerPage))
        }
        return acc
      }, [])
    },
    [rowsPerPage]
  )

  const isLastPage = useMemo(() => {
    if (!rawItems) return true
    return !sliceItemsPerRowsPerPage(rawItems)[page + 1]
  }, [rawItems, sliceItemsPerRowsPerPage, page])

  const onPageChange = useCallback(
    (_event: unknown, newPage: number) => {
      setPage(newPage)
    },
    [setPage]
  )

  const onRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10))
      setPage(0)
    },
    [setPage, setRowsPerPage]
  )

  return {
    createSortHandler,
    direction,
    displayItems,
    getComparator,
    isLastPage,
    onPageChange,
    onRowsPerPageChange,
    page,
    rowsPerPage,
    setPage,
    sliceItemsPerRowsPerPage,
    sortConfig,
    stableSortArray,
  }
}
